import { ApiRequestService } from 'src/app/shared/api-request.service';
import { AppService } from 'src/app/app.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment, {Moment} from 'moment';
import { site_statuses, site_types } from 'src/app/shared/shared-items';
import { UtilsService, GooglePlacePrediction } from 'src/app/shared/utils.service';
import { NgForm } from '@angular/forms';
import { SiteModel } from 'src/app/models/site.model';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

declare var google;

@Component({
  selector: 'app-admin-site-details-edit',
  templateUrl: './admin-site-details-edit.component.html',
  styleUrls: ['./admin-site-details-edit.component.scss']
})
export class AdminSiteDetailsEditComponent implements OnInit, OnDestroy {
  /**
   * Is the form being submitted?
   */
  inProgress = false;

  // the site model placeholder
  site: SiteModel = new SiteModel();

  site_types: string[] = site_types;

  site_statuses: string[] = site_statuses;

  site_risk_assessments: string[] = [
    'low',
    'moderate',
    'high',
    'critical'
  ];

  estimatedProjectStartDate: moment.Moment | null;
  estimatedProjectEndDate: moment.Moment | null;
  siteOpenTime: moment.Moment | null;
  siteCloseTime: moment.Moment | null;

  // Address lookup (Google Places Data API).
  addressPredictions: GooglePlacePrediction[] = [];
  private addressInput$: Subject<string> = new Subject<string>();
  private placesSessionToken: any = null;
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    // public service: SitesService,
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private apiRequest: ApiRequestService,
    public utils: UtilsService
  ) {}

  ngOnInit() {
    // get the site id
    this.site.id = Number(this.route.parent.snapshot.params['site_id']);

    // check if we are updating a site or creating a new site
    if (this.site.id) {
      // get the data from the api
      this.apiRequest
        .makeRequest('get', 'v2/admin/sites/' + this.site.id, {}, {}, {})
        .then((response) => {
          // apply the data
          this.site.apply(response.data);

          // Set the estimated site start date
          if (this.site.estimated_project_start_date) {
            this.estimatedProjectStartDate = moment.unix(
              this.site.estimated_project_start_date
            );
          }

          // Set the estimated site end date
          if (this.site.estimated_project_end_date) {
            this.estimatedProjectEndDate = moment.unix(
              this.site.estimated_project_end_date
            );
          }

          // Set the site opening time
          if (this.site.open_time) {
            this.siteOpenTime = this.toMoment(this.site.open_time);
          }

          // Set the site closing time
          if (this.site.close_time) {
            this.siteCloseTime = this.toMoment(this.site.close_time);
          }
        })
        .catch((error) => {
          this.utils.showModal('Error', error.message);
        });
    } else {
      // Set the site opening time
      if (this.site.open_time) {
        this.siteOpenTime = this.toMoment(this.site.open_time);
      }

      // Set the site closing time
      if (this.site.close_time) {
        this.siteCloseTime = this.toMoment(this.site.close_time);
      }

      // Set up the address lookup (Google Places Data API). No map here, so predictions are
      // region-biased to the configured default country only.
      this.setupAddressLookup();
    }
  }

  // get timezone output from timezone select component
  getOutputTimezone($event) {
    this.site.timezone = $event;
  }

  /**
   * Clean up subscriptions when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Wire up the address lookup using the Google Places Data API. Debounced query changes fetch
   * predictions which are shown in the Material autocomplete panel. Selecting a prediction resolves
   * the full formatted address.
   * @private
   */
  private setupAddressLookup(): void {
    this.addressInput$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe((value: string): void => {
      if (!value || value.trim().length < 3) {
        this.addressPredictions = [];
        return;
      }

      // Start a session on first keystroke of a new search.
      if (!this.placesSessionToken) {
        this.utils.createPlacesSessionToken()
          .then((token: any): void => {
            this.placesSessionToken = token;
          })
          .catch((): void => { /* token is optional; predictions still work without it */ });
      }

      this.utils.getPlacePredictions(value, this.placesSessionToken)
        .then((predictions: GooglePlacePrediction[]): void => {
          this.addressPredictions = predictions;
        })
        .catch((): void => {
          this.addressPredictions = [];
        });
    });
  }

  /**
   * Called as the user types in the address field. Keeps the model in sync (a free-text address is
   * still valid) and pushes the query into the debounced prediction stream.
   * @param value The current input value.
   */
  onAddressInputChange(value: string): void {
    this.site.location = value || '';
    this.addressInput$.next(value || '');
  }

  /**
   * Handle selection of an address prediction: resolve the place details and store the formatted
   * address.
   * @param prediction The selected prediction.
   */
  onSelectPrediction(prediction: GooglePlacePrediction): void {
    this.utils.getPlaceDetails(prediction.placePrediction)
      .then((details): void => {
        this.site.location = details.formattedAddress;
        this.addressPredictions = [];
        // The details fetch ends the billing session; start a fresh one for the next search.
        this.placesSessionToken = null;
      })
      .catch((): void => { /* ignore: user can still type a free-text address */ });
  }

  onSubmit(form: NgForm) {
    // make sure the form data is valid
    if (form.valid) {
      if (this.inProgress) {
        return;
      }

      this.inProgress = true;

      // Convert the estimated site start and end dates to a unix timestamps.
      if (this.estimatedProjectStartDate && this.estimatedProjectStartDate instanceof moment) {
        this.site.estimated_project_start_date = this.estimatedProjectStartDate.unix();
      } else {
        this.site.estimated_project_start_date = null;
      }

      if (this.estimatedProjectEndDate && this.estimatedProjectEndDate instanceof moment) {
        this.site.estimated_project_end_date = this.estimatedProjectEndDate.unix();
      } else {
        this.site.estimated_project_end_date = null;
      }

      // Convert the opening and closing time to a time string.
      if (this.siteOpenTime && this.siteOpenTime instanceof moment) {
        this.site.open_time = this.toTimeString(this.siteOpenTime);
      }

      if (this.siteCloseTime && this.siteCloseTime instanceof moment) {
        this.site.close_time = this.toTimeString(this.siteCloseTime);
      }

      // check if the site should be created or updated
      if (this.site.id) {
        this.apiRequest
          .makeRequest('put', 'v2/admin/sites/' + this.site.id, this.site, {}, {})
          .then((response) => {
            this.utils.showToast('The ' + this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site').toLowerCase() + ' was updated.');
            this.inProgress = false;
          })
          .finally(() => (this.inProgress = false));
      } else {
        // create a new site

        this.apiRequest
          .makeRequest('post', 'v2/admin/sites', this.site, {}, {})
          .then((response) => {
            this.utils.showToast('The ' + this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site').toLowerCase() + ' was created.');
            this.inProgress = false;

            // refresh the component to envoke change detection for the new route
            this.router
              .navigateByUrl('/RefreshComponent', { skipLocationChange: true })
              .then(() => {
                this.router.navigate([
                  '/admin/sites',
                  response.data.id,
                  'edit',
                  'details'
                ]);
              });
          })
          .finally(() => (this.inProgress = false));
      }
    } else {
      this.utils.showModal(
        'Form Error',
        'Please make sure you enter all fields with valid information.'
      );
    }
  }

  /**
   * Show the project start and end dates if the status of the site is any of:
   * - Pre-Construction
   * - Under Construction
   * - Hand Over Complete
   *
   * @param site_status The status of the site.
   */
  showProjectStartEndDates(site_status: string) {
    return (
      ['Pre-Construction', 'Under Construction', 'Hand Over Complete'].indexOf(
        site_status
      ) > -1
    );
  }

  /**
   * Retrieves the details of the output start date and time based on the provided event.
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputStartDateTimeDetails(event: any) {
    this.estimatedProjectStartDate = event;
  }

  /**
   * Retrieves the details of the output end date and time based on the provided event.
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputEndDateTimeDetails(event: any) {
    this.estimatedProjectEndDate = event;
  }

  /**
   * Retrieves the details of the output site opening time based on the provided event.
   * @param {any} event - The event object containing the output time details.
   * @return {void}
   */
  getOutputOpeningTimeDetails(event: any) {
    this.siteOpenTime = event;
  }

  /**
   * Retrieves the details of the output site closing time based on the provided event.
   * @param {any} event - The event object containing the output time details.
   * @return {void}
   */
  getOutputClosingTimeDetails(event: any) {
    this.siteCloseTime = event;
  }

  /**
   * Convert the given time string in 'HH:mm:ss' format to a moment object with today's date as the carrier.
   *
   * @param {string | null} time - The time string in 'HH:mm:ss' format to convert to a moment object. Can be null.
   * @return {moment | null} A moment object with the time from the input string and today's date as the carrier, or null if the input time is invalid.
   */
  private toMoment(time: string | null) {
    if (!time) { return null; }
    // moment parses time; date portion defaults to today.
    return moment(time, 'HH:mm:ss', true).isValid()
      ? moment(time, 'HH:mm:ss')
      : null;
  }

  /**
   * Convert a moment object to a string in 'HH:mm:ss' format.
   *
   * @param {Moment | null} val - The moment object to be converted to a string. Can be null.
   * @return {string | null} A string representing the time in 'HH:mm:ss' format, or null if the input is null.
   */
  private toTimeString(val: Moment | null) {
    return val ? val.format('HH:mm:ss') : null;
  }

}
